import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { FormGroup, Label } from 'reactstrap';
import CategoryModel from '../../../../../models/v2/category/category.model';
import { SelectMenuOptionType } from '../../../../../models/v2/general/select.model';
import { buildCategoryTree, filterCategories } from '../helpers/category-picker.helpers';
import { categoriesToOptions, categoriesToOptionArray } from '../helpers/category-select.helper';
import { categoryOption } from './CategoryOption';

interface CategoryPickerProps {
	id: string;
	label: string;
	categories: CategoryModel[];
	selectedValue: CategoryModel | CategoryModel[] | null;
	onChange: (selected: CategoryModel | CategoryModel[] | null) => void;
	placeholder?: string;
	isMulti?: boolean;
	disabled?: boolean;
}

const CategoryPicker: React.FC<CategoryPickerProps> = ({
	id,
	label,
	categories,
	selectedValue,
	onChange,
	placeholder,
	isMulti = false,
	disabled = false,
}) => {
	const { t } = useTranslation();
	const [hierarchicalCategories, setHierarchicalCategories] = useState<CategoryModel[]>([]);
	const [options, setOptions] = useState<SelectMenuOptionType[]>([]);
	const [searchText, setSearchText] = useState<string>('');

	useEffect(() => {
		if (!categories || categories.length === 0) {
			setHierarchicalCategories([]);
			setOptions([]);
			return;
		}

		const hierarchical = buildCategoryTree(categories);
		setHierarchicalCategories(hierarchical);

		// Initial options without filtering
		updateFilteredOptions(hierarchical, '');
	}, [categories]);

	useEffect(() => {
		// Update filtered options when search text changes
		updateFilteredOptions(hierarchicalCategories, searchText);
	}, [searchText, hierarchicalCategories]);

	const updateFilteredOptions = (hierarchical: CategoryModel[], search: string) => {
		let categoriesToShow: CategoryModel[];

		if (!search.trim()) {
			// No search - show all categories
			categoriesToShow = hierarchical;
		} else {
			// Apply hierarchical filtering that maintains parent-child relationships
			categoriesToShow = filterCategories(hierarchical, search);
		}

		// Convert to flat options for react-select
		const selectOptions = categoriesToOptions(categoriesToShow);
		setOptions(selectOptions);
	};

	const onCategorySelect = (categoriesData: SelectMenuOptionType | readonly SelectMenuOptionType[] | null | undefined) => {
		if (categoriesData) {
			if (isMulti) {
				const dataArray = Array.isArray(categoriesData) ? [...categoriesData] : [categoriesData];
				const categoriesDataForRedux = dataArray
					.filter((categoryData: SelectMenuOptionType) => !!categoryData.data)
					.map((categoryData: SelectMenuOptionType) => categoryData.data);
				onChange(categoriesDataForRedux.length > 0 ? categoriesDataForRedux : null);
			} else {
				const singleData = Array.isArray(categoriesData) ? categoriesData[0] : categoriesData;
				const selectedCategory = singleData && singleData.data ? (singleData.data as CategoryModel) : null;
				onChange(selectedCategory);
			}
		} else {
			onChange(null);
		}
	};

	const getCurrentValue = (): SelectMenuOptionType | SelectMenuOptionType[] | null => {
		if (!selectedValue) {
			return null;
		}

		if (isMulti) {
			const selectedArray = Array.isArray(selectedValue) ? selectedValue : [selectedValue];
			return categoriesToOptionArray(selectedArray);
		} else {
			const singleCategory = Array.isArray(selectedValue) ? selectedValue[0] : selectedValue;
			return singleCategory ? categoriesToOptionArray([singleCategory])[0] : null;
		}
	};

	const handleInputChange = (inputValue: string) => {
		setSearchText(inputValue);
	};

	return (
		<FormGroup>
			{label && <Label htmlFor={id}>{label}</Label>}
			<Select
				id={id}
				value={getCurrentValue()}
				options={options}
				onChange={onCategorySelect}
				onInputChange={handleInputChange}
				filterOption={() => true}
				isMulti={isMulti}
				isDisabled={disabled}
				formatOptionLabel={categoryOption}
				placeholder={placeholder || t('select')}
				noOptionsMessage={() => t('no_options')}
				isSearchable={true}
				isClearable={true}
				data-qa={id}
			/>
		</FormGroup>
	);
};

export default CategoryPicker;
